'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { AsyncActionResponse } from '@pioz/mlm-common';
import { PasswordInput, SubmitButton, TextInput } from '@pioz/mlm-skeleton/form-fields';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Form from 'next/form';

export const loginSchema = (t: { invalidEmail: string; required: string }) =>
  z.object({
    email: z.string({ required_error: t.required }).email(t.invalidEmail),
    password: z.string().min(1, t.required),
  });

export const LoginForm = ({
  forgotLink,
  registerLink,
  t,
  loginAction,
  captchaKey,
  errorsT,
}: {
  centered?: boolean;
  forgotLink: string;
  registerLink?: string;
  loginAction: (data: FormData) => AsyncActionResponse<{ redirect: string }>;
  captchaKey?: string;
  t: {
    username: string;
    password: string;
    submit: string;
    forgotPassword: string;
    createNewAccount: string;
  };
  errorsT: {
    invalidEmail: string;
    required: string;
  };
}) => {
  const { replace } = useRouter();
  const registrationkind = process.env.NEXT_PUBLIC_REGISTER_TYPE;

  // ** Form Hook
  const {
    register,
    setError,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'all',
    resolver: zodResolver(loginSchema(errorsT)),
  });

  const callSignIn = async (data: FormData) => {
    const response = await loginAction(data);
    if ('error' in response) {
      setError('email', { message: response.error.message });
      return;
    }
    replace(response.success.redirect);
  };

  const action = async (data: FormData) => {
    if (!captchaKey) {
      await callSignIn(data);
      return;
    }
    // @ts-ignore
    // eslint-disable-next-line no-undef
    grecaptcha.ready(() => {
      // @ts-ignore
      // eslint-disable-next-line no-undef
      grecaptcha.execute(captchaKey, { action: 'submit' }).then(async (token: string) => {
        data.append('recaptcha_token', token);
        await callSignIn(data);
      });
    });
  };

  return (
    <Form className="flex flex-col justify-center gap-3" action={action}>
      {!!registrationkind && (
        <input type="hidden" name="registrationkind" value={registrationkind} />
      )}
      <TextInput
        required
        data-testid="login-email"
        label={t.username}
        placeholder={t.username}
        error={errors.email}
        defaultValue={getValues('email')} // <- prevents data delete on form submit
        {...register('email', {
          onChange: (event) => {
            setValue('email', event.target.value.trim());
          },
        })}
      />
      <PasswordInput
        required
        label={t.password}
        placeholder={t.password}
        {...register('password', {
          onChange: (event) => {
            setValue('password', event.target.value.trim());
          },
        })}
        error={errors.password}
        defaultValue={getValues('password')} // <- prevents data delete on form submit
      />
      <div className="flex flex-col items-center gap-3">
        <SubmitButton className="btn btn-primary">{t.submit}</SubmitButton>
        {forgotLink && (
          <Link className="text-xs font-bold underline" color="inherit" href={forgotLink}>
            {t.forgotPassword}
          </Link>
        )}
        {registerLink && (
          <Link className="link text-sm" href={registerLink}>
            {t.createNewAccount}
          </Link>
        )}
      </div>
    </Form>
  );
};
